import { createFileRoute } from '@tanstack/react-router'
import { useSuspenseQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'

import { conceptQueryOptions, conceptStoredQueryOptions } from '#/api/concepts'
import type { ConceptDetail, ConceptSection } from '#/api/concepts'
import { hasPermission, useCan } from '#/user/me'
import {
  PageHeader,
  SectionLabel,
  displayTitleClass,
} from '#/components/page-header'
import { RouteError } from '#/components/route-error'
import { RoutePending } from '#/components/route-pending'
import { AnswerProse } from '#/components/ask-annwn/answer-prose'
import { ConceptStoredView } from '#/components/corpus/concept-stored'
import { CorpusBackLink } from '#/components/corpus/corpus-list'
import { FactStrip } from '#/components/corpus/fact-strip'
import { useCorpusLabels } from '#/components/corpus/labels'
import { PageContainer } from '#/components/page-container'
import { formatNumber } from '#/lib/format'

export const Route = createFileRoute(
  '/_authed/accounts/$accountId/corpus/$conceptId',
)({
  // What the account holds is read only for a reader who may read its
  // documents; the definition is for everyone in the account.
  loader: ({ context, params }) =>
    Promise.all([
      context.queryClient.ensureQueryData(
        conceptQueryOptions(params.conceptId),
      ),
      hasPermission(context.me, 'annwn:documents:read')
        ? context.queryClient.ensureQueryData(
            conceptStoredQueryOptions(params.accountId, params.conceptId),
          )
        : null,
    ]),
  pendingComponent: RoutePending,
  errorComponent: RouteError,
  component: ConceptPage,
})

// One concept: what it is, what this account holds for it, and its definition
// section by section, as the agents read it.
function ConceptPage() {
  const { t } = useTranslation()
  const { accountId, conceptId } = Route.useParams()
  const canReadDocuments = useCan('annwn:documents:read')
  const { conceptName } = useCorpusLabels()
  const { data: concept } = useSuspenseQuery(conceptQueryOptions(conceptId))

  return (
    <PageContainer width="wide">
      <div className="flex flex-col gap-6">
        <CorpusBackLink accountId={accountId}>
          {t('corpus.allConcepts')}
        </CorpusBackLink>
        <PageHeader
          title={conceptName(concept.id)}
          description={
            concept.summary ? (
              <span className="block max-w-3xl text-body-lg">
                {concept.summary}
              </span>
            ) : undefined
          }
        />
        <ConceptFacts concept={concept} />
      </div>

      {canReadDocuments ? (
        <section className="flex flex-col gap-4">
          <SectionLabel>{t('corpus.stored.title')}</SectionLabel>
          <StoredSection accountId={accountId} conceptId={conceptId} />
        </section>
      ) : null}

      <Definition concept={concept} />
    </PageContainer>
  )
}

// Where the concept's document stands, and how many failure modes it lists.
function ConceptFacts({ concept }: { concept: ConceptDetail }) {
  const { t, i18n } = useTranslation()
  const { codeName } = useCorpusLabels()
  return (
    <FactStrip
      facts={[
        [t('corpus.facts.version'), concept.version],
        [t('corpus.facts.status'), codeName('status', concept.meta.status)],
        [
          t('corpus.facts.failureModes'),
          formatNumber(concept.failure_modes, i18n.language),
        ],
      ]}
    />
  )
}

function StoredSection({
  accountId,
  conceptId,
}: {
  accountId: string
  conceptId: string
}) {
  const { data: stored } = useSuspenseQuery(
    conceptStoredQueryOptions(accountId, conceptId),
  )
  return <ConceptStoredView stored={stored} />
}

const sectionAnchor = (id: string) => `section-${id}`

function Definition({ concept }: { concept: ConceptDetail }) {
  const { t } = useTranslation()
  const title = useSectionTitle()
  return (
    <section className="flex flex-col gap-6">
      <SectionLabel>{t('corpus.definition')}</SectionLabel>
      <div className="grid gap-10 lg:grid-cols-[12rem_minmax(0,1fr)]">
        <nav
          aria-label={t('corpus.contents')}
          className="lg:sticky lg:top-8 lg:self-start"
        >
          <ol className="flex flex-col gap-1 text-body-sm">
            {concept.sections.map((section) => (
              <li key={section.id}>
                <a
                  href={`#${sectionAnchor(section.id)}`}
                  className="text-muted-foreground hover:text-foreground hover:underline"
                >
                  {title(section)}
                </a>
              </li>
            ))}
          </ol>
        </nav>
        <div className="flex min-w-0 flex-col gap-10">
          {concept.sections.map((section) => (
            <article
              key={section.id}
              id={sectionAnchor(section.id)}
              aria-labelledby={`${sectionAnchor(section.id)}-title`}
              className="flex scroll-mt-8 flex-col gap-3 border-t border-border pt-6"
            >
              <h2
                id={`${sectionAnchor(section.id)}-title`}
                className={displayTitleClass('sm')}
              >
                {title(section)}
              </h2>
              <AnswerProse content={section.text} />
            </article>
          ))}
        </div>
      </div>
    </section>
  )
}

// A section's heading as its document gives it: its title, after its number
// where the document numbers it, since the text cites sections by number.
function useSectionTitle() {
  const { t } = useTranslation()
  return (section: ConceptSection) =>
    section.number === null
      ? section.title
      : t('corpus.sectionNumber', {
          number: section.number,
          title: section.title,
        })
}
